import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  Grid,
  Rating,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { sellerReviewService } from "../../../../services/sellerReviews";

interface ReviewCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface CreateFormData {
  display_seller_name: string;
  display_reviewer_name: string;
  review_date: string;
  reliability_rating: number;
  response_speed_rating: number;
  engineer_efficiency_rating: number;
  honesty_rating: number;
  comment: string;
}

const todayISO = () => new Date().toISOString().split("T")[0];

const DEFAULT_FORM: CreateFormData = {
  display_seller_name: "",
  display_reviewer_name: "",
  review_date: todayISO(),
  reliability_rating: 0,
  response_speed_rating: 0,
  engineer_efficiency_rating: 0,
  honesty_rating: 0,
  comment: "",
};

const RATING_FIELDS: { key: keyof CreateFormData; labelKey: string; defaultLabel: string }[] = [
  { key: "reliability_rating",         labelKey: "reviews.reliability",        defaultLabel: "Reliability" },
  { key: "response_speed_rating",      labelKey: "reviews.responseSpeed",      defaultLabel: "Response Speed" },
  { key: "engineer_efficiency_rating", labelKey: "reviews.engineerEfficiency", defaultLabel: "Engineer Efficiency" },
  { key: "honesty_rating",             labelKey: "reviews.honesty",            defaultLabel: "Honesty" },
];

const ReviewCreateDialog: React.FC<ReviewCreateDialogProps> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateFormData, string>>>({});
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setFormData({ ...DEFAULT_FORM, review_date: todayISO() });
    setErrors({});
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CreateFormData, string>> = {};
    if (!formData.display_seller_name.trim())
      newErrors.display_seller_name = t("admin.sellerReviews.sellerNameRequired", { defaultValue: "Seller name is required" });
    if (!formData.display_reviewer_name.trim())
      newErrors.display_reviewer_name = t("admin.sellerReviews.reviewerNameRequired", { defaultValue: "Reviewer name is required" });
    const ratingKeys: (keyof CreateFormData)[] = [
      "reliability_rating", "response_speed_rating", "engineer_efficiency_rating", "honesty_rating",
    ];
    ratingKeys.forEach((key) => {
      if (!(formData[key] as number))
        newErrors[key] = t("admin.sellerReviews.ratingRequired", { defaultValue: "Rating is required" });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { error } = await sellerReviewService.createReview({
        display_seller_name:        formData.display_seller_name.trim(),
        display_reviewer_name:      formData.display_reviewer_name.trim(),
        reliability_rating:         formData.reliability_rating,
        response_speed_rating:      formData.response_speed_rating,
        engineer_efficiency_rating: formData.engineer_efficiency_rating,
        honesty_rating:             formData.honesty_rating,
        comment:                    formData.comment || undefined,
        created_at:                 formData.review_date
                                      ? new Date(formData.review_date).toISOString()
                                      : undefined,
      });
      if (error) throw error;
      onCreated();
      handleClose();
    } catch (err: any) {
      console.error("Error creating review:", err);
      alert(t("common.error", { defaultValue: "Failed to create review" }));
    } finally {
      setSaving(false);
    }
  };

  const setField = (field: keyof CreateFormData, value: string | number) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          {t("admin.sellerReviews.addReview", { defaultValue: "Add Review" })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("admin.sellerReviews.addReviewHint", {
            defaultValue: "Manually enter the seller, reviewer and date — no account required.",
          })}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Identity section */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem" }}
            >
              {t("admin.sellerReviews.reviewIdentity", { defaultValue: "Review Identity" })}
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t("admin.sellerReviews.sellerName", { defaultValue: "Seller / Company Name" })}
                value={formData.display_seller_name}
                onChange={(e) => setField("display_seller_name", e.target.value)}
                fullWidth
                required
                error={Boolean(errors.display_seller_name)}
                helperText={errors.display_seller_name}
                placeholder={t("admin.sellerReviews.sellerNamePlaceholder", { defaultValue: "e.g. Medtech Solutions" })}
              />
              <TextField
                label={t("admin.sellerReviews.reviewerName", { defaultValue: "Reviewed By" })}
                value={formData.display_reviewer_name}
                onChange={(e) => setField("display_reviewer_name", e.target.value)}
                fullWidth
                required
                error={Boolean(errors.display_reviewer_name)}
                helperText={errors.display_reviewer_name}
                placeholder={t("admin.sellerReviews.reviewerNamePlaceholder", { defaultValue: "e.g. Dr. Ahmed Hassan" })}
              />
              <TextField
                label={t("admin.sellerReviews.reviewDate", { defaultValue: "Review Date" })}
                type="date"
                value={formData.review_date}
                onChange={(e) => setField("review_date", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayISO() }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Ratings section */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: "uppercase", letterSpacing: 1, fontSize: "0.7rem" }}
            >
              {t("admin.sellerReviews.individualRatings", { defaultValue: "Individual Ratings" })}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {RATING_FIELDS.map(({ key, labelKey, defaultLabel }) => (
                <Grid key={key} size={{ xs: 12, sm: 6 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      {t(labelKey, { defaultValue: defaultLabel })}
                    </Typography>
                    <Rating
                      value={formData[key] as number}
                      onChange={(_, val) => setField(key, val ?? 0)}
                      precision={1}
                      size="large"
                    />
                    {errors[key] && (
                      <Typography variant="caption" color="error" display="block">
                        {errors[key]}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider />

          <TextField
            label={t("admin.sellerReviews.comment", { defaultValue: "Comment" })}
            value={formData.comment}
            onChange={(e) => setField("comment", e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder={t("admin.sellerReviews.commentPlaceholder", { defaultValue: "Optional customer feedback..." })}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          {t("common.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ background: "linear-gradient(135deg, #ff0080 0%, #00d4ff 100%)" }}
        >
          {saving
            ? t("common.saving", { defaultValue: "Saving..." })
            : t("admin.sellerReviews.createReview", { defaultValue: "Create Review" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewCreateDialog;
